import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { remapFieldPath } from '../schemaWalk';
import { getValueAtPath } from '../values';
import type { FieldDescriptor } from '../types';
import type { FieldRenderState } from './FieldRenderState';
import { FieldSwitch } from './FieldSwitch';

/** {type: "array", items: {type: "object", ...}} — e.g. "parts used", one card per row. */
export function RepeatingGroupField({ field, state }: { field: FieldDescriptor; state: FieldRenderState }) {
  const { t } = useTranslation();
  const rows = getValueAtPath(state.values, field.path);
  const rowCount = Array.isArray(rows) ? rows.length : 0;

  return (
    <div>
      <h3 className="text-sm font-semibold">{field.schema.title ?? field.key}</h3>
      <div className="mt-2 space-y-3">
        {Array.from({ length: rowCount }, (_, index) => (
          // Index as key: rows have no stable id of their own, and index IS
          // the row's actual identity here — removeRow already addresses a
          // row by position, so this matches rather than introducing a second
          // notion of "which row" that could disagree with it.
          <div key={index} className="rounded-lg border p-3" data-testid="repeating-group-row">
            <div className="space-y-3">
              {(field.itemFields ?? []).map((itemField) => (
                <FieldSwitch
                  key={itemField.key}
                  field={remapFieldPath(itemField, `${field.path}.${index}.${itemField.key}`)}
                  state={state}
                />
              ))}
            </div>
            <div className="mt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => state.removeRow(field.path, index)}
              >
                {t('forms.repeatingGroup.remove')}
              </Button>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-2">
        <Button type="button" variant="outline" size="sm" onClick={() => state.addRow(field.path)}>
          {t('forms.repeatingGroup.add', { item: field.schema.title ?? field.key })}
        </Button>
      </div>
    </div>
  );
}
